import { useState, useEffect } from "react";
import { Box, Menu, MenuItem, TextField } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { styles } from "./styles";
import CustomButton from "../CustomButton/CustomButton";
import {
  DeleteFolderResponse,
  Folder,
  PostFolderResponse,
} from "../../proto/api_pb";
import { toast } from "react-toastify";
import { deleteFolder, postFolder } from "../../services/folderService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import DeleteConfirmationModal from "../Modal/DeleteConfirmationModal";

const MoreOptionsMenu: React.FC<{
  folder: Folder;
  handleRefresh: () => Promise<void>;
}> = ({ folder, handleRefresh }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(folder.getName());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { getValidAccessToken } = useGetValidAccessToken();

  useEffect(() => {
    setNewName(folder.getName());
  }, [folder]);

  const handleClose = () => {
    setAnchorEl(null);
    setIsEditing(false);
  };

  const handleEditSubmit = async () => {
    if (!newName.trim()) {
      toast.error("Folder name cannot be empty");
      return;
    }
    setIsLoading(true);
    const newFolder = folder;
    newFolder.setName(newName.trim());
    const token = await getValidAccessToken();
    const res = await postFolder(token, newFolder);
    if (res instanceof PostFolderResponse) {
      toast.success("Folder updated successfully");
      await handleRefresh();
      setIsLoading(false);
      handleClose();
    } else {
      setIsLoading(false);
      toast.error("Fail to update folder");
    }
  };

  const onDelete = async () => {
    const token = await getValidAccessToken();
    const res = await deleteFolder(token, folder.getId());
    if (res instanceof DeleteFolderResponse) {
      toast.warn("Folder deleted successfully");
      await handleRefresh();
      handleClose();
    } else {
      console.log(res);
      toast.error("Fail to delete folder");
    }
  };

  return (
    <Box>
      <Box
        id="menu-button"
        aria-controls={open ? "menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={styles.folderMenuIcon}
      >
        <MoreVertIcon />
      </Box>
      <Menu
        id="menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { mt: 4 } }}
        sx={styles.moreVerticalIconMenu}
      >
        {isEditing ? (
          <Box sx={styles.editFolderNameModal}>
            <TextField
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
              size="small"
            />
            <CustomButton
              text="Save"
              onClick={handleEditSubmit}
              loading={isLoading}
            />
          </Box>
        ) : (
          <Box>
            <MenuItem onClick={() => setIsEditing(true)}>
              Edit folder name
            </MenuItem>
            <MenuItem onClick={() => setIsConfirmOpen(true)}>
              Delete folder
            </MenuItem>
          </Box>
        )}
      </Menu>
      <DeleteConfirmationModal
        message={"Are you sure you want to delete this folder?"}
        toggle={() => setIsConfirmOpen(!isConfirmOpen)}
        isMessageModalOpen={isConfirmOpen}
        handleConfirm={onDelete}
      />
    </Box>
  );
};

export default MoreOptionsMenu;
